import { Grid, Container, Paper, Typography } from '@mui/material';
import { format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GetLocationValueBoxForDateQuery,
  useAddEditLocationValueBoxEntryMutation,
  useDeleteLocationValueBoxEntryMutation,
  useGetLocationValueBoxForDateQuery,
} from '../generated/graphql';
import { FIXED_BOX_ORDER } from '../lib/constants';
import { DATE_FORMAT } from '../lib/date_time';
import { ValueBox } from './ValueBox';

interface ReportToTerminalValueBoxProps {
  selectedLocation: {
    id: number;
  };
  selectedDateAndDay: { date: string; day: string };
  refreshToggle: boolean;
  createBoxForTerminal: boolean;
  valueBoxFunction?: (data: GetLocationValueBoxForDateQuery) => void;
}

type BoxType = {
  boxId: number;
  entryId: number;
  boxNameText: string;
  boxValue: number | string;
  isReadOnly: boolean;
  isFixedBox: boolean;
  isYesNoBox: boolean;
  isAddedFromTerminal: boolean;
  isTimeBox: boolean;
  isAlphaNumeric: boolean;
};

export function ReportToTerminalValueBox(props: ReportToTerminalValueBoxProps) {
  const { t } = useTranslation();
  const {
    selectedLocation,
    selectedDateAndDay,
    refreshToggle,
    createBoxForTerminal,
    valueBoxFunction,
  } = props;
  const { id } = selectedLocation;
  const { date } = selectedDateAndDay;
  const curTerminalId = selectedLocation.id;
  const { data, loading, error, refetch } = useGetLocationValueBoxForDateQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    variables: {
      locationId: id,
      date: date,
      forTerminal: createBoxForTerminal,
    },
  });

  const [editValueBoxEntryApi] = useAddEditLocationValueBoxEntryMutation();
  const [deleteValueBoxEntryApi] = useDeleteLocationValueBoxEntryMutation();
  const [allBoxData, setAllBoxData] = useState<BoxType[]>([]);

  useEffect(() => {
    if (data && data.locationValueBoxesForDay && valueBoxFunction) {
      valueBoxFunction(data);
    }
  }, [data]);

  useEffect(() => {
    if (id && date) {
      refetch({
        locationId: id,
        date: date,
        forTerminal: createBoxForTerminal,
      });
    }
  }, [id, date, refreshToggle]);

  useEffect(() => {
    if (loading === false && data) {
      let finalBoxes: BoxType[] = [];
      if (
        data.locationValueBoxesForDay &&
        data.locationValueBoxesForDay.length > 0
      ) {
        data.locationValueBoxesForDay.forEach((b) => {
          if (
            b.isReadOnly &&
            (b.boxValue === '' || (b.boxValue === '0' && !b.isAlphaNumeric))
          ) {
          } else {
            finalBoxes = finalBoxes.concat(b);
          }
        });

        if (createBoxForTerminal) {
          finalBoxes = [...finalBoxes].sort((a, b) => {
            if (a.isFixedBox && b.isFixedBox) {
              return (
                (FIXED_BOX_ORDER[a.boxNameText] ?? 99) -
                (FIXED_BOX_ORDER[b.boxNameText] ?? 99)
              );
            }
            if (a.isFixedBox) return -1;
            if (b.isFixedBox) return 1;
            return a.boxNameText.localeCompare(b.boxNameText, undefined, {
              sensitivity: 'base',
            });
          });
        }

        setAllBoxData(
          finalBoxes.filter(
            (box, index, boxes) =>
              boxes.findIndex((candidate) => candidate.boxId === box.boxId) ===
              index,
          ),
        );
      } else {
        setAllBoxData([]);
      }
    }
  }, [data, loading]);

  if (loading) {
    return <>Loading...</>;
  }

  if (!data || error) {
    return (
      <>
        <Typography variant="subtitle1" style={{ paddingRight: '1%' }}>
          {`No box-${error?.message}`}
        </Typography>
      </>
    );
  }

  return (
    <Paper>
      <Container style={{ flexDirection: 'row' }}>
        {allBoxData.length > 0 ? (
          <div>
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              style={{ marginTop: 1 }}
            >
              {allBoxData.map((b) => {
                return (
                  <ValueBox
                    key={b.boxId}
                    boxData={b}
                    onValueChanged={(id, val, entryId) => {
                      if (val && val !== '') {
                        const dayOfDate = format(
                          parse(date, DATE_FORMAT, new Date()),
                          'EEE',
                        );

                        editValueBoxEntryApi({
                          variables: {
                            boxEntry: {
                              valueBoxId: id,
                              entryId: entryId ? entryId : 0,
                              boxNameText: b.boxNameText,
                              boxValue: val,
                              valueAddedDate: date,
                              valueAddedDay: dayOfDate,
                            },
                          },
                        }).then(() => {
                          refetch({
                            locationId: curTerminalId,
                            date: date,
                            forTerminal: createBoxForTerminal,
                          });
                        });
                      } else if (entryId && entryId !== 0 && val === '') {
                        deleteValueBoxEntryApi({
                          variables: {
                            entryId,
                          },
                        }).then(() => {
                          refetch({
                            locationId: curTerminalId,
                            date: date,
                            forTerminal: createBoxForTerminal,
                          });
                        });
                      }
                    }}
                  />
                );
              })}
            </Grid>
          </div>
        ) : (
          <Typography variant="subtitle1" style={{ paddingRight: '1%' }}>
            {`${t('resource.noBoxAdded.capitalized')}`}
          </Typography>
        )}
      </Container>
    </Paper>
  );
}
